import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { downloadBlob } from "@/lib/download";
import { FileDown, FileText, Loader2, Calendar } from "lucide-react";

const REPORTS = [
  { key: "complaints", title: "All Complaints", desc: "Complete complaint master export." },
  { key: "technician-payment", title: "Technician Payment Report", desc: "Closed complaints with technician payment, TADA, self-purchase." },
  { key: "brand-wise", title: "Brand-wise Complaints", desc: "Volume of complaints grouped by brand." },
  { key: "product-wise", title: "Product-wise Complaints", desc: "Volume of complaints grouped by product." },
  { key: "pending-parts", title: "Pending Parts", desc: "All complaints with parts pending, PNA, approved, or in transit." },
  { key: "pna", title: "PNA Report", desc: "Part Not Available cases needing market purchase." },
  { key: "part-approval", title: "Part Approval Report", desc: "Parts requested, approved/rejected, by approver and source." },
  { key: "closed", title: "Closed Complaints", desc: "All closed jobs with payment and CSAT." },
  { key: "ageing", title: "Ageing / Backlog", desc: "Open complaints with their ageing days." },
  { key: "csat", title: "CSAT Report", desc: "Customer satisfaction ratings." },
  { key: "sales-return", title: "Sales Return / Exchange", desc: "Sales returned and exchange cases." },
];

/** BS reports (filter via bs_year/bs_month/fiscal_year). AD reports use date_from/date_to. */
const BS_AWARE_KEYS = new Set(["complaints", "pna", "closed", "ageing", "csat", "sales-return", "brand-wise", "product-wise"]);

export default function Reports() {
  const [busy, setBusy] = useState("");
  const [mode, setMode] = useState("bs"); // default to BS (Nepali)
  const [opts, setOpts] = useState({ years: [], months: [], fiscal_years: [], current: {} });
  const [bsYear, setBsYear] = useState("");
  const [bsMonth, setBsMonth] = useState("");
  const [fy, setFy] = useState("");
  const [adFrom, setAdFrom] = useState("");
  const [adTo, setAdTo] = useState("");

  useEffect(() => {
    api.get("/reports/bs-options")
      .then((r) => {
        const o = r.data || {};
        setOpts(o);
        if (o.current?.bs_year) setBsYear(String(o.current.bs_year));
        if (o.current?.bs_month) setBsMonth(String(o.current.bs_month));
      })
      .catch(() => { /* silent — picker just stays empty */ });
  }, []);

  const buildQuery = (key) => {
    if (!BS_AWARE_KEYS.has(key)) return "";
    if (mode === "bs") {
      const parts = [];
      if (bsYear) parts.push(`bs_year=${bsYear}`);
      if (bsMonth) parts.push(`bs_month=${bsMonth}`);
      if (fy) parts.push(`fiscal_year=${encodeURIComponent(fy)}`);
      return parts.length ? "&" + parts.join("&") : "";
    }
    const parts = [];
    if (adFrom) parts.push(`date_from=${adFrom}`);
    if (adTo) parts.push(`date_to=${adTo}`);
    return parts.length ? "&" + parts.join("&") : "";
  };

  const download = (key, fmt) => {
    const token = `${key}-${fmt}`;
    setBusy(token);
    downloadBlob({
      url: `/reports/${key}?fmt=${fmt}${buildQuery(key)}`,
      filename: `${key}.${fmt}`,
      onStart: (s) => { if (!s) setBusy(""); },
    });
  };

  const periodLabel = mode === "bs"
    ? (fy ? `FY ${fy}` : `${bsMonth ? (opts.months.find((m) => m.value === parseInt(bsMonth))?.label || bsMonth) + " · " : ""}${bsYear || "—"}`)
    : (adFrom || adTo ? `${adFrom || "…"} → ${adTo || "…"}` : "All dates");

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Download operational reports in Excel or PDF.</p>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-slate-500" />
              <div className="text-sm font-semibold text-slate-900">Date filter</div>
              <div className="text-xs text-slate-500 ml-1 font-mono bg-slate-50 px-2 py-0.5 rounded" data-testid="period-label">
                {periodLabel}
              </div>
            </div>
            <div className="flex gap-2" data-testid="date-mode-toggle">
              <Button size="sm" variant={mode === "bs" ? "default" : "outline"}
                      className={mode === "bs" ? "bg-slate-900 text-white" : ""}
                      onClick={() => setMode("bs")} data-testid="mode-bs">BS (Nepali)</Button>
              <Button size="sm" variant={mode === "ad" ? "default" : "outline"}
                      className={mode === "ad" ? "bg-slate-900 text-white" : ""}
                      onClick={() => setMode("ad")} data-testid="mode-ad">AD (English)</Button>
            </div>
          </div>

          {mode === "bs" ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <label className="text-xs text-slate-600 space-y-1">
                BS Year
                <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                        value={bsYear} onChange={(e) => { setBsYear(e.target.value); setFy(""); }}
                        data-testid="bs-year-select">
                  <option value="">— Any year —</option>
                  {opts.years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
              <label className="text-xs text-slate-600 space-y-1">
                BS Month
                <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                        value={bsMonth} onChange={(e) => { setBsMonth(e.target.value); setFy(""); }}
                        data-testid="bs-month-select">
                  <option value="">— Any month —</option>
                  {opts.months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <label className="text-xs text-slate-600 space-y-1">
                Fiscal Year (overrides month)
                <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                        value={fy} onChange={(e) => { setFy(e.target.value); if (e.target.value) { setBsMonth(""); } }}
                        data-testid="bs-fy-select">
                  <option value="">— None —</option>
                  {opts.fiscal_years.map((f) => <option key={f} value={f}>FY {f}</option>)}
                </select>
              </label>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="text-xs text-slate-600 space-y-1">
                From
                <input type="date" value={adFrom} onChange={(e) => setAdFrom(e.target.value)}
                       className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                       data-testid="ad-from-input" />
              </label>
              <label className="text-xs text-slate-600 space-y-1">
                To
                <input type="date" value={adTo} onChange={(e) => setAdTo(e.target.value)}
                       className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                       data-testid="ad-to-input" />
              </label>
            </div>
          )}

          <div className="text-[10px] text-slate-500">
            {mode === "bs"
              ? "Reports group by Nepali (BS) month. Excel exports include BS Date + BS Month columns."
              : "Reports group by AD (English) date range."}
            {" "}Reports without a date column (e.g. Technician Payment, Pending Parts) ignore this filter.
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r) => (
          <Card key={r.key} data-testid={`report-card-${r.key}`}>
            <CardContent className="p-5">
              <div className="font-heading font-semibold text-slate-900">
                {r.title}
                {BS_AWARE_KEYS.has(r.key) && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">date-filterable</span>
                )}
              </div>
              <div className="text-sm text-slate-500 mt-1 mb-4 min-h-[40px]">{r.desc}</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={busy === `${r.key}-xlsx`}
                        onClick={() => download(r.key, "xlsx")} data-testid={`download-${r.key}-xlsx`}>
                  {busy === `${r.key}-xlsx`
                    ? <Loader2 size={14} className="mr-1 animate-spin" />
                    : <FileDown size={14} className="mr-1" />}
                  Excel
                </Button>
                <Button variant="outline" size="sm" disabled={busy === `${r.key}-pdf`}
                        onClick={() => download(r.key, "pdf")} data-testid={`download-${r.key}-pdf`}>
                  {busy === `${r.key}-pdf`
                    ? <Loader2 size={14} className="mr-1 animate-spin" />
                    : <FileText size={14} className="mr-1" />}
                  PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
